import { config } from "../data/config";

// Login sends user-service checks, keyed by path. The action names must match
// RecaptchaService in user-service or the token is treated as invalid.
const ACTIONS: Record<string, string> = {
  "/v1/auth/otp/generate": "otp_generate",
  "/v1/auth/squid/otp/generate": "squid_otp_generate",
  "/v1/squid/onboard": "squid_onboard",
  "/v1/auth/partner/otp/generate": "partner_otp_generate",
  "/v1/auth/partner/code/otp/send": "partner_code_otp_send",
  "/v1/auth/partner/code/verify": "partner_code_verify",
};
const TIMEOUT_MS = 5000;

let scriptLoad: Promise<void> | undefined;

/** Loads Google's reCAPTCHA Enterprise script once. Call it when a login view opens. */
export function loadRecaptcha(): Promise<void> {
  if (typeof window === "undefined" || !config.recaptchaSiteKey) {
    return Promise.resolve();
  }
  scriptLoad ??= new Promise<void>((resolve) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(config.recaptchaSiteKey)}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoad = undefined; // retry on the next send
      resolve();
    };
    document.head.appendChild(script);
  });
  return scriptLoad;
}

async function fetchToken(action: string): Promise<string | null> {
  await loadRecaptcha();
  const enterprise = (window as any).grecaptcha?.enterprise;
  if (!enterprise) return null;
  return await new Promise<string | null>((resolve) =>
    enterprise.ready(() =>
      enterprise
        .execute(config.recaptchaSiteKey, { action })
        .then(resolve, () => resolve(null))
    )
  );
}

/**
 * `X-Recaptcha-Token` with a fresh token when the request is one of the login
 * sends above on our API, else `{}`. Never throws; after 5 s it gives up and
 * the request goes without a token, so the backend decides.
 */
export async function recaptchaHeaders(
  url: string,
  method = "POST"
): Promise<Record<string, string>> {
  if (typeof window === "undefined" || method.toUpperCase() !== "POST") {
    return {};
  }
  const target = new URL(url, window.location.href);
  const action = ACTIONS[target.pathname.replace(/\/$/, "")];
  if (!action || target.origin !== new URL(config.baseUrl).origin) return {};

  const token = await Promise.race([
    fetchToken(action).catch(() => null),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), TIMEOUT_MS)),
  ]);
  return token ? { "X-Recaptcha-Token": token } : {};
}
